import React, { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useSocketEvent, useSocketState } from '../helpers/useSocket'

function buildMatchMatrix(sets, entrants) {
	const matrix = {}
	entrants.forEach((e) => {
		matrix[e.id] = {}
	})

	sets.forEach((set) => {
		const slot0 = set.slots?.[0]
		const slot1 = set.slots?.[1]
		if (!slot0?.entrant || !slot1?.entrant) return

		const p0 = slot0.entrant
		const p1 = slot1.entrant

		matrix[p0.id][p1.id] = {
			rowScore: p0.score,
			colScore: p1.score,
			state: set.state,
			winnerId: set.winnerId,
			rowId: p0.id,
		}
		matrix[p1.id][p0.id] = {
			rowScore: p1.score,
			colScore: p0.score,
			state: set.state,
			winnerId: set.winnerId,
			rowId: p1.id,
		}
	})

	return matrix
}

function getCellResult(cell) {
	if (!cell) return null
	const { rowScore, colScore, state, winnerId, rowId } = cell

	if (state !== 3) return { text: null, isWin: null }

	const isDQRow = rowScore === -1
	const isDQCol = colScore === -1

	if (isDQRow) return { text: 'DQ - \u2713', isWin: false }
	if (isDQCol) return { text: '\u2713 - DQ', isWin: true }

	const isWin = winnerId === rowId
	return { text: `${rowScore} - ${colScore}`, isWin }
}

function calcTotals(sets, entrantId) {
	let wins = 0,
		losses = 0,
		gameWins = 0,
		gameLosses = 0

	sets.forEach((set) => {
		if (set.state !== 3) return
		const mySlot = set.slots?.find((s) => s.entrant?.id === entrantId)
		const theirSlot = set.slots?.find(
			(s) => s.entrant?.id !== entrantId && s.entrant,
		)
		if (!mySlot?.entrant) return

		if (set.winnerId === entrantId) wins++
		else losses++

		const myScore = mySlot.entrant.score
		const theirScore = theirSlot?.entrant?.score ?? 0
		if (myScore > 0) gameWins += myScore
		if (theirScore > 0) gameLosses += theirScore
	})

	return { wins, losses, gameWins, gameLosses }
}

function getPlayerDetails(playerName, players) {
	if (!players || !playerName) return null
	return (
		players.find(
			(p) => p.name === playerName || p.startggName === playerName,
		) ?? null
	)
}

function PlayerGroupGrid({ phaseGroup, groupLabel, players }) {
	const entrants = phaseGroup.standings?.nodes?.map((s) => s.entrant) || []
	const sets = phaseGroup.sets?.nodes || []

	if (entrants.length === 0) return null

	const matrix = buildMatchMatrix(sets, entrants)

	const totalsMap = {}
	entrants.forEach((e) => {
		totalsMap[e.id] = calcTotals(sets, e.id)
	})

	const seedMap = new Map()
	;(phaseGroup.seeds?.nodes ?? []).forEach((node) => {
		seedMap.set(node.entrant?.id, node.seedNum)
	})
	const sorted = [...entrants].sort(
		(a, b) =>
			(seedMap.get(a.id) ?? Infinity) - (seedMap.get(b.id) ?? Infinity),
	)

	return (
		<div className="inline-block max-w-4xl">
			<h2 className="rrbracket_groupLabel text-4xl font-semibold mb-3 text-[#aaaaaa] tracking-widest uppercase text-center">
				Group {groupLabel}
			</h2>
			<div className="overflow-x-auto rounded-xl bg-black/40 py-4">
				<table className="border-separate border-spacing-[3px] table-fixed">
					<thead>
						<tr>
							<th></th>
							{sorted.map((e) => (
								<th
									key={e.id}
									className="p-0 overflow-visible"
									style={{
										width: '5rem',
										maxWidth: '5rem',
										minWidth: '5rem',
									}}
								>
									<div
										className="relative h-24"
										style={{ width: '5rem' }}
									>
										<div className="absolute bottom-0 left-1/2 -rotate-45 origin-bottom-left whitespace-nowrap text-[0.8rem] font-bold text-[#e8e8e8] uppercase">
											{getPlayerDetails(e.name, players)
												?.name ?? e.name}
										</div>
									</div>
								</th>
							))}
							<th className="w-20 h-9 text-[0.78rem] font-bold text-[#e8e8e8] text-center align-bottom whitespace-nowrap pl-2">
								<span>W - L</span>
							</th>
						</tr>
					</thead>
					<tbody>
						{sorted.map((row) => {
							const t = totalsMap[row.id]
							return (
								<tr key={row.id}>
									<td
										className="h-9 font-bold text-[#e8e8e8] text-right pr-4 overflow-hidden uppercase"
										style={{
											width: '9rem',
											maxWidth: '9rem',
											minWidth: '7rem',
										}}
									>
										{(() => {
											const details = getPlayerDetails(
												row.name,
												players,
											)
											const displayName =
												details?.name ?? row.name
											const teamIcon =
												details?.teamPictureUrl
											return (
												<div className="flex items-center justify-end gap-1">
													{teamIcon && (
														<img
															src={teamIcon}
															className="h-5 w-5 object-contain flex-shrink-0"
															alt=""
														/>
													)}
													<span>{displayName}</span>
												</div>
											)
										})()}
									</td>
									{sorted.map((col) => {
										if (row.id === col.id) {
											return (
												<td
													key={col.id}
													className="w-20 h-9 bg-[#2a2a2a] rounded"
												/>
											)
										}
										const cell = matrix[row.id]?.[col.id]
										const result = getCellResult(cell)
										const cellCls =
											result?.isWin === true
												? 'border border-[#4caf50] text-[#4caf50]'
												: result?.isWin === false
													? 'border border-[#e53935] text-[#e53935]'
													: 'border border-[#444444] text-[#ccc]'
										return (
											<td
												key={col.id}
												className={`w-20 h-9 text-white font-medium rounded text-center whitespace-nowrap bg-[#313131] ${cellCls}${!result?.text ? ' italic' : ''}`}
											>
												{result?.text || '0 - 0'}
											</td>
										)
									})}
									<td className="w-20 h-9 text-center pl-2 whitespace-nowrap">
										<span className="text-[0.85rem] font-bold text-[#e8e8e8]">
											{t.wins} - {t.losses}
										</span>
										<br />
										<span className="text-[0.72rem] text-[#d4d4d4]">
											{t.gameWins} - {t.gameLosses}
										</span>
									</td>
								</tr>
							)
						})}
					</tbody>
				</table>
			</div>
		</div>
	)
}

function RoundRobinDisplay() {
	const [searchParams] = useSearchParams()
	const id = searchParams.get('id')
	const [tournament] = useSocketState('Tournament')
	const [config, setConfig] = useState({ cssTheme: 'default.css' })

	useSocketEvent('updateConfig', (newdata) => {
		setConfig(newdata)
	})

	// Find the phase matching the URL id param
	let foundPhase = null

	tournament?.bracket?.events?.forEach((event) => {
		event?.phases?.forEach((phase) => {
			if (
				phase.bracketType === 'ROUND_ROBIN' &&
				String(phase.id) === String(id)
			) {
				foundPhase = phase
			}
		})
	})

	return (
		<div className="min-h-dvh text-[#e8e8e8]">
			<link
				rel="stylesheet"
				type="text/css"
				href={`/themes/${config.cssTheme}`}
			/>
			{!foundPhase && (
				<div className="text-[#888888] text-base mt-8">
					{id
						? `No Round Robin stage found with ID: ${id}`
						: 'No ID specified.'}
				</div>
			)}
			{foundPhase && (
				<div className="flex flex-row flex-wrap w-full justify-evenly">
					{foundPhase.phaseGroups?.nodes?.map((pg, i) => (
						<PlayerGroupGrid
							key={pg.id}
							phaseGroup={pg}
							groupLabel={String.fromCharCode(65 + i)}
							players={tournament?.config?.players}
						/>
					))}
				</div>
			)}
		</div>
	)
}

export default RoundRobinDisplay
